import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DataSet from 'common-ui/components/DataSet'
import { RecursiveDataSet } from 'common-ui/components/RecursiveDataSet'

import type { AiConversationDetail, AiConversationModelTrace, AiConversationTurn } from 'common-billing-server/types'
import type { AiInferenceResponse } from 'common/api/v1/types'

import { useBillingApi } from '../../api/billing/billing-context'
import { formatDateTime } from '../../format-date'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import Wrapper from '../common/Wrapper'

const responseText = (response: AiInferenceResponse): string =>
    response.content
        .filter((block) => block.type === 'text')
        .map((block) => block.text)
        .join('\n')

const displayTrace = (trace: AiConversationModelTrace): Record<string, unknown> =>
    JSON.parse(
        JSON.stringify(trace, (key, value) =>
            key === 'data' && typeof value === 'string' ? `[base64 image: ${value.length} chars]` : value
        )
    ) as Record<string, unknown>

const ModelTrace = ({ trace }: { trace?: AiConversationModelTrace }) => {
    if (!trace) return null
    return (
        <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Model and tool trace</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <RecursiveDataSet object={displayTrace(trace)} sentenceCase />
            </AccordionDetails>
        </Accordion>
    )
}

interface ModelConversationProps {
    response?: AiInferenceResponse
    trace?: AiConversationModelTrace
}

const ModelConversation = ({ response, trace }: ModelConversationProps) => {
    const model = response?.billing?.model ?? trace?.response?.billing?.model ?? 'Model pending'
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="overline">Assistant response</Typography>
                    <Typography variant="h4" sx={{ overflowWrap: 'anywhere' }}>
                        {model}
                    </Typography>
                </Box>
                {response ? (
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{responseText(response)}</Typography>
                ) : (
                    <Typography color="text.secondary">The model is still working.</Typography>
                )}
                <ModelTrace trace={trace} />
            </Stack>
        </Paper>
    )
}

const ConversationTurn = ({ turn }: { turn: AiConversationTurn }) => {
    return (
        <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="h3">{formatDateTime(turn.userMessage.createdAt)} UTC</Typography>
                    <Chip size="small" label={turn.status} />
                    {turn.scopeOutcome ? <Chip size="small" variant="outlined" label={turn.scopeOutcome} /> : null}
                </Stack>

                <Box>
                    <Typography variant="overline">User</Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                        {turn.userMessage.content || '[Image-only message]'}
                    </Typography>
                    {turn.userMessage.images.length > 0 ? (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto' }}>
                            {turn.userMessage.images.map((image, index) => (
                                <Box
                                    component="img"
                                    key={`${turn.userMessageId}-${index}`}
                                    src={`data:${image.mediaType};base64,${image.data}`}
                                    alt={`User attachment ${index + 1}`}
                                    sx={{ maxWidth: 360, maxHeight: 240, objectFit: 'contain', borderRadius: 1 }}
                                />
                            ))}
                        </Stack>
                    ) : null}
                </Box>

                {turn.assistantResponse || turn.mainTrace ? (
                    <ModelConversation response={turn.assistantResponse} trace={turn.mainTrace} />
                ) : null}

                {turn.error ? (
                    <Typography color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                        {turn.error}
                    </Typography>
                ) : null}
                {turn.scopeTrace ? (
                    <Accordion disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Scope classifier trace</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <RecursiveDataSet object={displayTrace(turn.scopeTrace)} sentenceCase />
                        </AccordionDetails>
                    </Accordion>
                ) : null}
            </Stack>
        </Paper>
    )
}

export const AiConversationDetailsPage = () => {
    const { installationId, threadId } = useParams()
    const billingApi = useBillingApi()
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [conversation, setConversation] = useState<AiConversationDetail>()
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        if (!installationId || !threadId) {
            addFailedOperation({ id: 'load-ai-conversation', message: 'The AI conversation ID is missing.' })
            setLoading(false)
            return
        }
        removeFailedOperation('load-ai-conversation')
        setLoading(true)
        try {
            setConversation(await billingApi.getAiConversation(installationId, threadId))
        } catch (error) {
            addFailedOperation({
                id: 'load-ai-conversation',
                message: 'The AI conversation could not be loaded.',
                error,
                retryFn: () => void load(),
            })
        } finally {
            setLoading(false)
        }
    }, [billingApi, installationId, threadId])

    useEffect(() => {
        void load()
    }, [load])

    return (
        <Wrapper title={conversation?.title ?? 'AI conversation'}>
            {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
                    <CircularProgress aria-label="Loading AI conversation" />
                </Box>
            ) : conversation ? (
                <Stack spacing={2}>
                    <Paper sx={{ p: 2 }}>
                        <DataSet
                            values={{
                                Customer: conversation.customerName,
                                Installation: conversation.installationHost,
                                'Edge user': conversation.edgeUsername,
                                'Thread ID': conversation.threadId,
                                'Last activity': `${formatDateTime(conversation.updatedAt)} UTC`,
                            }}
                        />
                    </Paper>
                    {conversation.turns.map((turn) => (
                        <ConversationTurn key={turn.jobId} turn={turn} />
                    ))}
                </Stack>
            ) : null}
            {errorBanner}
        </Wrapper>
    )
}
